import React from 'react';
import { useTranslation } from 'react-i18next';

import { Icon } from '../../../../components';
import { CREATIVE_GRADIENT_STOPS } from '../../../../theme/tokens';
import {
  gradientCss,
  TEXT_SCALE,
  type CreativeDraft,
  type CreativeFormat,
  type ElementId,
  type ExtraElement,
  type ExtraKind,
  type Selection,
  type Vec,
} from '../../custom-formats';
import { STICKERS } from '../../custom-stickers';
import * as S from './CustomStudio.styles';
import * as E from './EditorStage.styles';

/** Base canvas long-edge in CSS px (the stage transform then scales it to fit / zoom). */
const BASE_LONG = 460;
/** Keep the canvas off the stage edges at the fit scale. */
const STAGE_PAD = 56;
const MIN_ZOOM = 0.25;
const MAX_ZOOM = 4;

const clamp01 = (n: number): number => Math.min(1, Math.max(0, n));
const clampZoom = (n: number): number => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, n));
const clampOff = (n: number): number => Math.min(0.5, Math.max(-0.5, n));
const pct = (n: number): string => `${n * 100}%`;
const gradientById = (id: string): { angle: number; stops: readonly string[] } =>
  CREATIVE_GRADIENT_STOPS.find((g) => g.id === id) ?? CREATIVE_GRADIENT_STOPS[0];

/** A draggable/resizable target: one of the fixed elements, or an extra by id. */
type Target = ElementId | { extra: string };
const isExtra = (t: Target | Selection): t is { extra: string } => typeof t === 'object' && t !== null && 'extra' in t;

type DragState =
  | { kind: 'el'; target: Target; sx: number; sy: number; start: Vec }
  | { kind: 'pan'; sx: number; sy: number; start: Vec }
  | { kind: 'bgpan'; sx: number; sy: number; startOff: Vec }
  | {
      kind: 'resize';
      target: Target;
      mode: 'corner' | 'width' | 'height';
      cx: number;
      cy: number;
      rot: number;
      startDist: number;
      startAxis: number;
      startWidth: number;
      startHeight: number;
      startScale: number;
    }
  | { kind: 'rotate'; target: Target; cx: number; cy: number; startAngle: number; startRot: number }
  | null;

export type EditorStageProps = {
  format: CreativeFormat;
  draft: CreativeDraft;
  logoUrl?: string;
  /** Brand logo URLs offered in the "+" menu (logoUrl plus any family variants). */
  brandLogos?: string[];
  selected: Selection;
  onSelect: (s: Selection) => void;
  onChange: (patch: (d: CreativeDraft) => CreativeDraft) => void;
  /** Add an extra element (logo / sticker) — CustomStudio mints the id + selects it. */
  onAddExtra: (kind: ExtraKind, src: string) => void;
};

/**
 * The interactive editor canvas: a zoomable, pannable stage holding the creative. Elements (the
 * fixed headline/subheadline/CTA/logo AND any added extras) are selected + dragged directly, text
 * is edited inline (double-click), and everything resizes (corner = uniform, edges = width/height)
 * and rotates via on-canvas handles. The "+" menu adds logos/stickers.
 *
 * Coordinates: each element stores its CENTRE as a 0..1 fraction of the canvas; drag deltas convert
 * to fractions via the canvas's live rect, so they're correct at any zoom with no manual maths.
 */
export const EditorStage = ({
  format,
  draft,
  logoUrl,
  brandLogos = [],
  selected,
  onSelect,
  onChange,
  onAddExtra,
}: EditorStageProps): React.ReactElement => {
  const { t } = useTranslation();
  const stageRef = React.useRef<HTMLDivElement>(null);
  const canvasRef = React.useRef<HTMLDivElement>(null);
  const dragRef = React.useRef<DragState>(null);
  const editNodeRef = React.useRef<HTMLElement | null>(null);
  const editValueRef = React.useRef<string>('');

  const long = Math.max(format.w, format.h);
  const baseW = (format.w / long) * BASE_LONG;
  const baseH = (format.h / long) * BASE_LONG;

  const [fit, setFit] = React.useState(1);
  const [zoom, setZoom] = React.useState(1);
  const [pan, setPan] = React.useState<Vec>({ x: 0, y: 0 });
  const [editing, setEditing] = React.useState<ElementId | null>(null);
  const [menuOpen, setMenuOpen] = React.useState(false);
  const viewScale = fit * zoom;

  // Clamp pan so the canvas can never be dragged fully off-screen — always keep MARGIN px on view.
  const clampInfo = React.useRef({ vs: viewScale, bw: baseW, bh: baseH });
  clampInfo.current = { vs: viewScale, bw: baseW, bh: baseH };
  const clampPan = React.useCallback((p: Vec): Vec => {
    const s = stageRef.current?.getBoundingClientRect();
    if (!s) return p;
    const { vs, bw, bh } = clampInfo.current;
    const MARGIN = 56;
    const maxX = Math.max(0, s.width / 2 + (bw * vs) / 2 - MARGIN);
    const maxY = Math.max(0, s.height / 2 + (bh * vs) / 2 - MARGIN);
    return { x: Math.max(-maxX, Math.min(maxX, p.x)), y: Math.max(-maxY, Math.min(maxY, p.y)) };
  }, []);

  React.useLayoutEffect(() => {
    const el = stageRef.current;
    if (!el) return undefined;
    const measure = (): void => {
      const r = el.getBoundingClientRect();
      const f = Math.min((r.width - STAGE_PAD) / baseW, (r.height - STAGE_PAD) / baseH);
      setFit(f > 0 ? f : 1);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [baseW, baseH]);

  React.useEffect(() => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
    setEditing(null);
  }, [format.id]);

  // Ctrl/⌘ + wheel zooms (native, non-passive to stop the page zooming); a plain wheel pans.
  React.useEffect(() => {
    const el = stageRef.current;
    if (!el) return undefined;
    const onWheel = (e: WheelEvent): void => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        setZoom((z) => clampZoom(z * (e.deltaY < 0 ? 1.1 : 0.9)));
      } else {
        setPan((p) => clampPan({ x: p.x - e.deltaX, y: p.y - e.deltaY }));
      }
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [clampPan]);

  React.useLayoutEffect(() => {
    if (!editing) return;
    const node = editNodeRef.current;
    if (!node) return;
    node.textContent = editValueRef.current;
    node.focus();
    const sel = window.getSelection();
    const range = document.createRange();
    range.selectNodeContents(node);
    sel?.removeAllRanges();
    sel?.addRange(range);
  }, [editing]);

  /* ── Fixed-element accessors (by ElementId) ── */
  const posOf = (id: ElementId): Vec => (id === 'logo' ? draft.logo.pos : id === 'cta' ? draft.cta.pos : draft[id].pos);
  const setPos = (id: ElementId, p: Vec): void =>
    onChange((d) => {
      if (id === 'logo') return { ...d, logo: { ...d.logo, pos: p } };
      if (id === 'cta') return { ...d, cta: { ...d.cta, pos: p } };
      return { ...d, [id]: { ...d[id], pos: p } };
    });
  const setText = (id: 'headline' | 'subheadline' | 'cta', text: string): void =>
    onChange((d) => (id === 'cta' ? { ...d, cta: { ...d.cta, text } } : { ...d, [id]: { ...d[id], text } }));
  const scaleOf = (id: ElementId): number =>
    id === 'logo' ? draft.logo.scale : id === 'cta' ? draft.cta.scale : draft[id].scale;
  const setScaleOf = (id: ElementId, s: number): void =>
    onChange((d) => {
      if (id === 'logo') return { ...d, logo: { ...d.logo, scale: s } };
      if (id === 'cta') return { ...d, cta: { ...d.cta, scale: s } };
      return { ...d, [id]: { ...d[id], scale: s } };
    });
  const rotOf = (id: ElementId): number =>
    id === 'logo' ? draft.logo.rot : id === 'cta' ? draft.cta.rot : draft[id].rot;
  const setRotOf = (id: ElementId, r: number): void =>
    onChange((d) => {
      if (id === 'logo') return { ...d, logo: { ...d.logo, rot: r } };
      if (id === 'cta') return { ...d, cta: { ...d.cta, rot: r } };
      return { ...d, [id]: { ...d[id], rot: r } };
    });
  const widthOf = (id: ElementId): number => (id === 'cta' ? draft.cta.width : id === 'logo' ? 1 : draft[id].width);
  const setWidthOf = (id: ElementId, wv: number): void =>
    onChange((d) => {
      if (id === 'cta') return { ...d, cta: { ...d.cta, width: wv } };
      if (id === 'logo') return d;
      return { ...d, [id]: { ...d[id], width: wv } };
    });
  const setHeightCta = (hv: number): void => onChange((d) => ({ ...d, cta: { ...d.cta, height: hv } }));

  /* ── Extra accessors + target-generic accessors ── */
  const extraById = (id: string): ExtraElement | undefined => draft.extras.find((e) => e.id === id);
  const updateExtra = (id: string, fn: (e: ExtraElement) => ExtraElement): void =>
    onChange((d) => ({ ...d, extras: d.extras.map((e) => (e.id === id ? fn(e) : e)) }));
  const posOfT = (tg: Target): Vec => (isExtra(tg) ? extraById(tg.extra)?.pos ?? { x: 0.5, y: 0.5 } : posOf(tg));
  const setPosT = (tg: Target, p: Vec): void =>
    isExtra(tg) ? updateExtra(tg.extra, (e) => ({ ...e, pos: p })) : setPos(tg, p);
  const scaleOfT = (tg: Target): number => (isExtra(tg) ? extraById(tg.extra)?.scale ?? 0.2 : scaleOf(tg));
  const setScaleT = (tg: Target, s: number): void =>
    isExtra(tg) ? updateExtra(tg.extra, (e) => ({ ...e, scale: s })) : setScaleOf(tg, s);
  const rotOfT = (tg: Target): number => (isExtra(tg) ? extraById(tg.extra)?.rot ?? 0 : rotOf(tg));
  const setRotT = (tg: Target, r: number): void =>
    isExtra(tg) ? updateExtra(tg.extra, (e) => ({ ...e, rot: r })) : setRotOf(tg, r);
  const centerOfT = (tg: Target): { cx: number; cy: number } | null => {
    const r = canvasRef.current?.getBoundingClientRect();
    if (!r) return null;
    const p = posOfT(tg);
    return { cx: r.left + p.x * r.width, cy: r.top + p.y * r.height };
  };
  /** Images (logo/extras) resize by scale only (a small canvas fraction). */
  const clampImgScale = (s: number): number => Math.min(0.9, Math.max(0.03, s));
  const clampW = (v: number): number => Math.min(1.5, Math.max(0.05, v));
  const clampH = (v: number): number => Math.min(1, Math.max(0.02, v));

  const capture = (pointerId: number): void => {
    try {
      stageRef.current?.setPointerCapture(pointerId);
    } catch {
      /* capture is best-effort */
    }
  };

  const startElDrag =
    (tg: Target) =>
    (e: React.PointerEvent): void => {
      // While editing this (text) element, let the click place the caret (but don't bubble).
      if (!isExtra(tg) && editing === tg) {
        e.stopPropagation();
        return;
      }
      e.stopPropagation();
      onSelect(tg);
      if (editing) setEditing(null);
      setMenuOpen(false);
      dragRef.current = { kind: 'el', target: tg, sx: e.clientX, sy: e.clientY, start: posOfT(tg) };
      capture(e.pointerId);
    };

  const onCanvasPointerDown = (e: React.PointerEvent): void => {
    e.stopPropagation();
    onSelect('background');
    if (editing) setEditing(null);
    setMenuOpen(false);
    // An image background pans; a colour/gradient one just moves the view.
    dragRef.current =
      draft.background.kind === 'image'
        ? { kind: 'bgpan', sx: e.clientX, sy: e.clientY, startOff: draft.background.offset }
        : { kind: 'pan', sx: e.clientX, sy: e.clientY, start: pan };
    capture(e.pointerId);
  };

  const onStagePointerDown = (e: React.PointerEvent): void => {
    onSelect(null);
    if (editing) setEditing(null);
    setMenuOpen(false);
    dragRef.current = { kind: 'pan', sx: e.clientX, sy: e.clientY, start: pan };
    capture(e.pointerId);
  };

  const onPointerMove = (e: React.PointerEvent): void => {
    const d = dragRef.current;
    if (!d) return;
    if (d.kind === 'el') {
      const r = canvasRef.current?.getBoundingClientRect();
      if (!r || r.width === 0 || r.height === 0) return;
      setPosT(d.target, {
        x: clamp01(d.start.x + (e.clientX - d.sx) / r.width),
        y: clamp01(d.start.y + (e.clientY - d.sy) / r.height),
      });
    } else if (d.kind === 'bgpan') {
      const r = canvasRef.current?.getBoundingClientRect();
      if (!r) return;
      const nx = clampOff(d.startOff.x - (e.clientX - d.sx) / r.width);
      const ny = clampOff(d.startOff.y - (e.clientY - d.sy) / r.height);
      onChange((dd) =>
        dd.background.kind === 'image' ? { ...dd, background: { ...dd.background, offset: { x: nx, y: ny } } } : dd,
      );
    } else if (d.kind === 'resize') {
      const dx = e.clientX - d.cx;
      const dy = e.clientY - d.cy;
      const tg = d.target;
      if (d.mode === 'corner') {
        let r = Math.hypot(dx, dy) / d.startDist;
        if (isExtra(tg)) {
          setScaleT(tg, clampImgScale(d.startScale * r));
        } else if (tg === 'logo') {
          setScaleOf('logo', clampImgScale(d.startScale * r));
        } else {
          // Text/CTA: scale font + box by the SAME clamped ratio → grows without reflow.
          const rMax = Math.min(6 / d.startScale, 1.5 / d.startWidth, tg === 'cta' ? 1 / d.startHeight : Infinity);
          const rMin = Math.max(0.25 / d.startScale, 0.05 / d.startWidth, tg === 'cta' ? 0.02 / d.startHeight : 0);
          r = Math.min(rMax, Math.max(rMin, r));
          setScaleOf(tg, d.startScale * r);
          setWidthOf(tg, d.startWidth * r);
          if (tg === 'cta') setHeightCta(d.startHeight * r);
        }
      } else if (!isExtra(tg)) {
        // Edge = one dimension, along the element's LOCAL axis (rotation-aware), symmetric.
        const ar = (-d.rot * Math.PI) / 180;
        const localX = dx * Math.cos(ar) - dy * Math.sin(ar);
        const localY = dx * Math.sin(ar) + dy * Math.cos(ar);
        if (d.mode === 'width') setWidthOf(tg, clampW((d.startWidth * Math.abs(localX)) / d.startAxis));
        else setHeightCta(clampH((d.startHeight * Math.abs(localY)) / d.startAxis));
      }
    } else if (d.kind === 'rotate') {
      const ang = Math.atan2(e.clientY - d.cy, e.clientX - d.cx);
      let deg = d.startRot + ((ang - d.startAngle) * 180) / Math.PI;
      const snapped = Math.round(deg / 15) * 15;
      if (Math.abs(deg - snapped) < 4) deg = snapped;
      setRotT(d.target, Math.round(deg));
    } else {
      setPan(clampPan({ x: d.start.x + (e.clientX - d.sx), y: d.start.y + (e.clientY - d.sy) }));
    }
  };

  const onPointerUp = (e: React.PointerEvent): void => {
    dragRef.current = null;
    try {
      stageRef.current?.releasePointerCapture(e.pointerId);
    } catch {
      /* release is best-effort */
    }
  };

  const startResize =
    (tg: Target, mode: 'corner' | 'width' | 'height') =>
    (e: React.PointerEvent): void => {
      e.stopPropagation();
      const c = centerOfT(tg);
      if (!c) return;
      onSelect(tg);
      const dx = e.clientX - c.cx;
      const dy = e.clientY - c.cy;
      const rot = rotOfT(tg);
      const ar = (-rot * Math.PI) / 180;
      const localX = dx * Math.cos(ar) - dy * Math.sin(ar);
      const localY = dx * Math.sin(ar) + dy * Math.cos(ar);
      dragRef.current = {
        kind: 'resize',
        target: tg,
        mode,
        cx: c.cx,
        cy: c.cy,
        rot,
        startDist: Math.hypot(dx, dy) || 1,
        startAxis: (mode === 'height' ? Math.abs(localY) : Math.abs(localX)) || 1,
        startWidth: isExtra(tg) ? 1 : widthOf(tg),
        startHeight: !isExtra(tg) && tg === 'cta' ? draft.cta.height : 1,
        startScale: scaleOfT(tg),
      };
      capture(e.pointerId);
    };
  const startRotate =
    (tg: Target) =>
    (e: React.PointerEvent): void => {
      e.stopPropagation();
      const c = centerOfT(tg);
      if (!c) return;
      onSelect(tg);
      dragRef.current = {
        kind: 'rotate',
        target: tg,
        cx: c.cx,
        cy: c.cy,
        startAngle: Math.atan2(e.clientY - c.cy, e.clientX - c.cx),
        startRot: rotOfT(tg),
      };
      capture(e.pointerId);
    };

  const startEdit = (id: 'headline' | 'subheadline' | 'cta', value: string) => (): void => {
    editValueRef.current = value;
    onSelect(id);
    setEditing(id);
  };
  const onEditKeyDown = (e: React.KeyboardEvent): void => {
    e.stopPropagation();
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      (e.currentTarget as HTMLElement).blur();
    } else if (e.key === 'Escape') {
      (e.currentTarget as HTMLElement).blur();
    }
  };
  const editRef =
    (id: 'headline' | 'subheadline' | 'cta') =>
    (node: HTMLElement | null): void => {
      if (editing === id) editNodeRef.current = node;
    };
  const placeholder = (label: string): React.ReactNode => <span style={{ opacity: 0.5 }}>{label}</span>;

  // Corner cursors follow the diagonal (rotation aside): TL/BR = nwse, TR/BL = nesw.
  const CORNERS: ReadonlyArray<{ l: string; t: string; cursor: string }> = [
    { l: '0%', t: '0%', cursor: 'nwse-resize' },
    { l: '100%', t: '0%', cursor: 'nesw-resize' },
    { l: '0%', t: '100%', cursor: 'nesw-resize' },
    { l: '100%', t: '100%', cursor: 'nwse-resize' },
  ];
  // Edge handles: text gets width (left/right); the button also gets height (top/bottom); images none.
  const edgesFor = (tg: Target): ReadonlyArray<{ l: string; t: string; cursor: string; mode: 'width' | 'height' }> => {
    if (isExtra(tg) || tg === 'logo') return [];
    const w: { l: string; t: string; cursor: string; mode: 'width' } = {
      l: '0%',
      t: '50%',
      cursor: 'ew-resize',
      mode: 'width',
    };
    const wide = [w, { ...w, l: '100%' }];
    if (tg !== 'cta') return wide;
    const h: { l: string; t: string; cursor: string; mode: 'height' } = {
      l: '50%',
      t: '0%',
      cursor: 'ns-resize',
      mode: 'height',
    };
    return [...wide, h, { ...h, t: '100%' }];
  };
  const renderHandles = (tg: Target): React.ReactNode => {
    // The Movable is only scaled by the stage zoom (box model, not element scale), so counter-scale
    // every fixed length by viewScale alone — controls stay constant no matter the element size.
    const hz = (px: number): number => px / (viewScale || 1);
    return (
      <E.HandleFrame>
        <E.Ring style={{ borderWidth: hz(1.5), borderRadius: hz(6) }} />
        {CORNERS.map((c) => (
          <E.Handle
            key={`c-${c.l}-${c.t}`}
            onPointerDown={startResize(tg, 'corner')}
            style={{
              left: c.l,
              top: c.t,
              width: hz(11),
              height: hz(11),
              borderWidth: hz(1.5),
              borderRadius: hz(3),
              cursor: c.cursor,
              transform: 'translate(-50%, -50%)',
            }}
          />
        ))}
        {edgesFor(tg).map((eh) => (
          <E.Handle
            key={`e-${eh.l}-${eh.t}`}
            onPointerDown={startResize(tg, eh.mode)}
            style={{
              left: eh.l,
              top: eh.t,
              width: eh.mode === 'width' ? hz(6) : hz(18),
              height: eh.mode === 'width' ? hz(18) : hz(6),
              borderWidth: hz(1.5),
              borderRadius: hz(3),
              cursor: eh.cursor,
              transform: 'translate(-50%, -50%)',
            }}
          />
        ))}
        <E.RotateStem
          style={{ left: '50%', top: 0, width: hz(1.5), height: hz(16), transform: 'translate(-50%, -100%)' }}
        />
        <E.RotateHandle
          onPointerDown={startRotate(tg)}
          style={{
            left: '50%',
            top: 0,
            width: hz(12),
            height: hz(12),
            borderWidth: hz(1.5),
            transform: `translate(-50%, calc(-100% - ${hz(16)}px))`,
          }}
        />
      </E.HandleFrame>
    );
  };

  const bg = draft.background;
  const bgCss = bg.kind === 'color' ? bg.value : gradientCss(gradientById(bg.value));
  const bgImage = bg.kind === 'image' ? bg.imageUrl : undefined;
  const logos = Array.from(new Set([logoUrl, ...brandLogos].filter((u): u is string => Boolean(u))));

  const add = (kind: ExtraKind, src: string): void => {
    onAddExtra(kind, src);
    setMenuOpen(false);
  };

  return (
    <E.Stage
      ref={stageRef}
      onPointerDown={onStagePointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
    >
      <E.Transform style={{ transform: `translate(-50%, -50%) translate(${pan.x}px, ${pan.y}px) scale(${viewScale})` }}>
        <S.Canvas ref={canvasRef} style={{ width: baseW, height: baseH }} onPointerDown={onCanvasPointerDown}>
          <S.BgFill $bg={bgCss} $image={bgImage} $ox={bg.offset.x} $oy={bg.offset.y} />
          {bgImage && bg.scrim > 0 ? <S.Scrim $alpha={bg.scrim} /> : null}

          {/* Headline */}
          <E.Movable
            style={{
              left: pct(draft.headline.pos.x),
              top: pct(draft.headline.pos.y),
              transform: `translate(-50%, -50%) rotate(${draft.headline.rot}deg)`,
            }}
            $selected={selected === 'headline'}
            $editing={editing === 'headline'}
            onPointerDown={startElDrag('headline')}
            onDoubleClick={startEdit('headline', draft.headline.text)}
          >
            <S.CanvasHeadline
              $scale={TEXT_SCALE[draft.headline.size] * draft.headline.scale}
              $width={draft.headline.width}
              $color={draft.headline.color}
              $align={draft.headline.align}
              contentEditable={editing === 'headline'}
              suppressContentEditableWarning
              ref={editRef('headline')}
              onInput={
                editing === 'headline' ? (e) => setText('headline', e.currentTarget.textContent ?? '') : undefined
              }
              onBlur={editing === 'headline' ? () => setEditing(null) : undefined}
              onKeyDown={editing === 'headline' ? onEditKeyDown : undefined}
              style={{
                outline: 'none',
                userSelect: editing === 'headline' ? 'text' : 'none',
                WebkitUserSelect: editing === 'headline' ? 'text' : 'none',
              }}
            >
              {editing === 'headline' ? undefined : draft.headline.text || placeholder(t('Add a headline'))}
            </S.CanvasHeadline>
            {selected === 'headline' && editing !== 'headline' ? renderHandles('headline') : null}
          </E.Movable>

          {/* Subheadline */}
          <E.Movable
            style={{
              left: pct(draft.subheadline.pos.x),
              top: pct(draft.subheadline.pos.y),
              transform: `translate(-50%, -50%) rotate(${draft.subheadline.rot}deg)`,
            }}
            $selected={selected === 'subheadline'}
            $editing={editing === 'subheadline'}
            onPointerDown={startElDrag('subheadline')}
            onDoubleClick={startEdit('subheadline', draft.subheadline.text)}
          >
            <S.CanvasSub
              $scale={TEXT_SCALE[draft.subheadline.size] * draft.subheadline.scale}
              $width={draft.subheadline.width}
              $color={draft.subheadline.color}
              $align={draft.subheadline.align}
              contentEditable={editing === 'subheadline'}
              suppressContentEditableWarning
              ref={editRef('subheadline')}
              onInput={
                editing === 'subheadline' ? (e) => setText('subheadline', e.currentTarget.textContent ?? '') : undefined
              }
              onBlur={editing === 'subheadline' ? () => setEditing(null) : undefined}
              onKeyDown={editing === 'subheadline' ? onEditKeyDown : undefined}
              style={{
                outline: 'none',
                userSelect: editing === 'subheadline' ? 'text' : 'none',
                WebkitUserSelect: editing === 'subheadline' ? 'text' : 'none',
              }}
            >
              {editing === 'subheadline' ? undefined : draft.subheadline.text || placeholder(t('Add a subheadline'))}
            </S.CanvasSub>
            {selected === 'subheadline' && editing !== 'subheadline' ? renderHandles('subheadline') : null}
          </E.Movable>

          {/* CTA */}
          {draft.cta.show ? (
            <E.Movable
              style={{
                left: pct(draft.cta.pos.x),
                top: pct(draft.cta.pos.y),
                transform: `translate(-50%, -50%) rotate(${draft.cta.rot}deg)`,
              }}
              $selected={selected === 'cta'}
              $editing={editing === 'cta'}
              onPointerDown={startElDrag('cta')}
              onDoubleClick={startEdit('cta', draft.cta.text)}
            >
              <S.CanvasCta
                $bg={draft.cta.color}
                $scale={draft.cta.scale}
                $width={draft.cta.width}
                $height={draft.cta.height}
                contentEditable={editing === 'cta'}
                suppressContentEditableWarning
                ref={editRef('cta')}
                onInput={editing === 'cta' ? (e) => setText('cta', e.currentTarget.textContent ?? '') : undefined}
                onBlur={editing === 'cta' ? () => setEditing(null) : undefined}
                onKeyDown={editing === 'cta' ? onEditKeyDown : undefined}
                style={{
                  outline: 'none',
                  userSelect: editing === 'cta' ? 'text' : 'none',
                  WebkitUserSelect: editing === 'cta' ? 'text' : 'none',
                }}
              >
                {editing === 'cta' ? undefined : draft.cta.text || placeholder(t('Button'))}
              </S.CanvasCta>
              {selected === 'cta' && editing !== 'cta' ? renderHandles('cta') : null}
            </E.Movable>
          ) : null}

          {/* Logo */}
          {draft.logo.show && logoUrl ? (
            <E.Movable
              style={{
                left: pct(draft.logo.pos.x),
                top: pct(draft.logo.pos.y),
                transform: `translate(-50%, -50%) rotate(${draft.logo.rot}deg)`,
              }}
              $selected={selected === 'logo'}
              onPointerDown={startElDrag('logo')}
            >
              <S.CanvasLogo $scale={draft.logo.scale} src={logoUrl} alt="" draggable={false} />
              {selected === 'logo' ? renderHandles('logo') : null}
            </E.Movable>
          ) : null}

          {/* Extras (added logos / stickers), drawn on top */}
          {draft.extras.map((ex) => (
            <E.Movable
              key={ex.id}
              style={{
                left: pct(ex.pos.x),
                top: pct(ex.pos.y),
                transform: `translate(-50%, -50%) rotate(${ex.rot}deg)`,
              }}
              $selected={isExtra(selected) && selected.extra === ex.id}
              onPointerDown={startElDrag({ extra: ex.id })}
            >
              <S.CanvasLogo $scale={ex.scale} src={ex.src} alt="" draggable={false} />
              {isExtra(selected) && selected.extra === ex.id ? renderHandles({ extra: ex.id }) : null}
            </E.Movable>
          ))}
        </S.Canvas>
      </E.Transform>

      <E.ZoomBar onPointerDown={(e) => e.stopPropagation()}>
        <E.ZoomBtn type="button" onClick={() => setZoom((z) => clampZoom(z * 0.9))} aria-label={t('Zoom out')}>
          −
        </E.ZoomBtn>
        <E.ZoomLabel>{Math.round(zoom * 100)}%</E.ZoomLabel>
        <E.ZoomBtn type="button" onClick={() => setZoom((z) => clampZoom(z * 1.1))} aria-label={t('Zoom in')}>
          +
        </E.ZoomBtn>
        <E.ZoomBtn
          type="button"
          onClick={() => {
            setZoom(1);
            setPan({ x: 0, y: 0 });
          }}
          aria-label={t('Fit to screen')}
        >
          {t('Fit')}
        </E.ZoomBtn>
      </E.ZoomBar>

      {menuOpen ? (
        <E.AddMenu onPointerDown={(e) => e.stopPropagation()}>
          <E.AddGroup>
            <E.AddGroupTitle text="Brand logos" />
            {logos.length > 0 ? (
              <E.AddGrid>
                {logos.map((src) => (
                  <E.AddItem key={src} type="button" onClick={() => add('logo', src)} aria-label={t('Add logo')}>
                    <img src={src} alt="" />
                  </E.AddItem>
                ))}
              </E.AddGrid>
            ) : (
              <E.AddEmpty text="No brand logo on file yet." />
            )}
          </E.AddGroup>
          <E.AddGroup>
            <E.AddGroupTitle text="Stickers" />
            <E.AddGrid>
              {STICKERS.map((s) => (
                <E.AddItem key={s.id} type="button" onClick={() => add('sticker', s.src)} aria-label={t(s.label)}>
                  <img src={s.src} alt="" />
                </E.AddItem>
              ))}
            </E.AddGrid>
          </E.AddGroup>
        </E.AddMenu>
      ) : null}
      <E.AddBtn
        type="button"
        onPointerDown={(e) => e.stopPropagation()}
        onClick={() => setMenuOpen((o) => !o)}
        aria-label={t('Add an element')}
      >
        <Icon name="plus" size={20} />
      </E.AddBtn>
    </E.Stage>
  );
};

export default EditorStage;
